import resetStoreOnSessionEnd from '../actions/resetStoreOnSessionEnd';
import setStaticSearchScopeData from '../actions/setStaticSearchScopeData';
import { updateSearchHistoryUserConfig } from '../legacyActions/searchHistory';
import store from '../store/store';
import SearchProvider from '../types/SearchProvider';
import getSearchProvider from '../utils/getSearchProvider';
import getSuggestionPillFromSelectedNode from '../utils/getSuggestionPillFromSelectedNode';
import isMailSearchAction from '../utils/isMailSearchAction';
import { createLazyOrchestrator } from 'owa-bundling';
import { isFeatureEnabled } from 'owa-feature-flags';
import { lazyLogFloodgateActivity } from 'owa-floodgate-feedback-view';
import { is3SFeatureEnabled } from 'owa-mail-feature-flags';
import { SearchScenarioId } from 'owa-search-next';
import { setSearchActive } from 'owa-suite-header-apis';
import { orchestrator } from 'satcheljs';
import {
    executeSearchStartSearchSessionService,
    executeSearchEndSearchSessionService,
} from 'owa-search-service';
import {
    getSuggestions,
    lazyGetSearchSessionGuid,
    onPillSuggestionSelected,
    onSearchSessionEnded,
    onSearchSessionStarted,
    startSearch,
} from 'owa-search-next-lazy';

export const onSearchSessionStartedOrchestrator = createLazyOrchestrator(
    onSearchSessionStarted,
    'lazyOnSearchSessionStarted',
    async actionMessage => {
        const { scenarioId, shouldStartSearch, actionSource } = actionMessage;

        if (!isMailSearchAction(scenarioId)) {
            return;
        }

        // Initialize search scope data (based on currently selected node).
        setStaticSearchScopeData();

        /**
         * Once search scope is set, then we can determine the search provider
         * that will be used during this search session.
         */
        const searchProvider = getSearchProvider();

        /**
         * Make the appropriate initialization call based on which search provider
         * is being used during the session.
         *
         * TODO: Determine if we should move 3S init here.
         */
        if (searchProvider === SearchProvider.ExecuteSearch) {
            const getSearchSessionGuid = await lazyGetSearchSessionGuid.import();
            executeSearchStartSearchSessionService(
                getSearchSessionGuid(scenarioId),
                store.staticSearchScope
            );
        }

        // Add pill to the search box (if applicable).
        const suggestionPill = getSuggestionPillFromSelectedNode();
        if (suggestionPill) {
            onPillSuggestionSelected(
                suggestionPill,
                false /* suggestionSelected */,
                SearchScenarioId.Mail
            );
        }

        // Put header into "search mode".
        if (isFeatureEnabled('fwk-o365shellheader')) {
            setSearchActive(true);
        }

        // Start search or get suggestions (depending on action message).
        if (shouldStartSearch) {
            startSearch(actionSource, SearchScenarioId.Mail);
        } else {
            getSuggestions(scenarioId);
        }
    }
);

orchestrator(onSearchSessionEnded, async actionMessage => {
    const { scenarioId, searchSessionGuid, latestTraceId } = actionMessage;

    if (!isMailSearchAction(scenarioId)) {
        return;
    }

    // Fire request required to end a search session (if ExecuteSearch).
    if (!is3SFeatureEnabled('sea-substrateQuery')) {
        executeSearchEndSearchSessionService(searchSessionGuid);
    }

    // Save search history (if dirty).
    if (store.isSearchHistoryDirty) {
        updateSearchHistoryUserConfig();
    }

    // Reset store.
    resetStoreOnSessionEnd();
    setStaticSearchScopeData(true /* shouldClear */);

    if (isFeatureEnabled('fwk-o365shellheader')) {
        setSearchActive(false);
    }

    if (store.searchNumber !== 0) {
        const logFloodgateActivity = await lazyLogFloodgateActivity.import();
        logFloodgateActivity('SearchExecuted', latestTraceId);
    }
});
